if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        this.unlockPageScroll();
        super.hide();
      }

      lockPageScroll() {
        if (this._scrollLocked) return;
        this._scrollLockY = window.scrollY || document.documentElement.scrollTop || 0;
        document.documentElement.classList.add('quick-add-modal-open');
        document.body.classList.add('quick-add-modal-open');
        if (window.lenis && typeof window.lenis.stop === 'function') {
          window.lenis.stop();
        }
        this._scrollLocked = true;
      }

      unlockPageScroll() {
        if (!this._scrollLocked) return;
        this.unbindModalScroll();
        document.documentElement.classList.remove('quick-add-modal-open');
        document.body.classList.remove('quick-add-modal-open');
        if (window.lenis && typeof window.lenis.start === 'function') {
          window.lenis.start();
          window.lenis.scrollTo(this._scrollLockY || 0, { immediate: true });
        }
        this._scrollLocked = false;
      }

      getModalScrollEl() {
        return this.querySelector('.quick-add-modal__content');
      }

      logScrollDiagnostics(label = 'open') {
        const scrollEl = this.getModalScrollEl();
        const infoEl = this.querySelector('.quick-add-modal__content-info');
        if (!scrollEl) {
          console.warn('[quick-add-modal] scroll diagnostics: no scroll container found');
          return;
        }

        const cs = window.getComputedStyle(scrollEl);
        const canScroll = scrollEl.scrollHeight > scrollEl.clientHeight + 1;
        const payload = {
          label,
          scrollEl: scrollEl.className,
          clientHeight: scrollEl.clientHeight,
          scrollHeight: scrollEl.scrollHeight,
          scrollTop: scrollEl.scrollTop,
          canScroll,
          overflowY: cs.overflowY,
          overflowX: cs.overflowX,
          height: cs.height,
          maxHeight: cs.maxHeight,
          touchAction: cs.touchAction,
          transform: cs.transform,
          lenisStopped: window.lenis?.isStopped ?? window.lenis?.stopped ?? 'unknown',
          bodyOverflow: window.getComputedStyle(document.body).overflow,
          htmlOverflow: window.getComputedStyle(document.documentElement).overflow,
          bodyClasses: document.body.className,
        };

        if (infoEl) {
          const infoCs = window.getComputedStyle(infoEl);
          payload.contentInfo = {
            clientHeight: infoEl.clientHeight,
            scrollHeight: infoEl.scrollHeight,
            overflowY: infoCs.overflowY,
            height: infoCs.height,
          };
        }

        const productInfo = scrollEl.querySelector('product-info');
        if (productInfo) {
          payload.productInfo = {
            clientHeight: productInfo.clientHeight,
            scrollHeight: productInfo.scrollHeight,
            overflowY: window.getComputedStyle(productInfo).overflowY,
          };
        }

        console.group('[quick-add-modal] scroll diagnostics');
        console.log(payload);
        if (!canScroll) {
          console.warn(
            '[quick-add-modal] NOT scrollable: scrollHeight (%s) <= clientHeight (%s)',
            scrollEl.scrollHeight,
            scrollEl.clientHeight
          );
          console.log('[quick-add-modal] ancestor overflow chain:');
          let node = scrollEl.parentElement;
          let depth = 0;
          while (node && depth < 8) {
            const ncs = window.getComputedStyle(node);
            console.log(`  ${depth}. <${node.tagName.toLowerCase()}>`, {
              className: node.className,
              overflow: ncs.overflow,
              overflowY: ncs.overflowY,
              height: ncs.height,
              maxHeight: ncs.maxHeight,
              touchAction: ncs.touchAction,
            });
            node = node.parentElement;
            depth += 1;
          }
        } else {
          console.log('[quick-add-modal] scrollable ✓');
        }
        console.groupEnd();
      }

      ensureScrollContainer() {
        const scrollEl = this.getModalScrollEl();
        if (!scrollEl) return;

        scrollEl.style.overflowY = 'auto';
        scrollEl.style.overflowX = 'hidden';
        scrollEl.style.webkitOverflowScrolling = 'touch';
        scrollEl.style.touchAction = 'pan-y';
        scrollEl.style.overscrollBehavior = 'contain';

        const infoEl = this.querySelector('.quick-add-modal__content-info');
        if (infoEl) {
          infoEl.style.overflow = 'visible';
          infoEl.style.height = 'auto';
          infoEl.style.minHeight = '0';
          infoEl.style.flex = 'none';
        }

        scrollEl.querySelectorAll('product-info').forEach((el) => {
          el.style.minHeight = '0';
          el.style.height = 'auto';
          el.style.overflow = 'visible';
        });
      }

      bindModalScroll() {
        this.unbindModalScroll();
        const scrollEl = this.getModalScrollEl();
        if (!scrollEl) return;

        this.ensureScrollContainer();

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.logScrollDiagnostics('after-layout');
          });
        });

        let touchStartY = 0;
        let touchStartScrollTop = 0;

        const onWheel = (event) => {
          const { scrollTop, scrollHeight, clientHeight } = scrollEl;
          const maxScroll = scrollHeight - clientHeight;
          if (maxScroll <= 0) {
            console.warn('[quick-add-modal] wheel ignored — container not scrollable', {
              scrollHeight,
              clientHeight,
            });
            return;
          }
          event.preventDefault();
          event.stopPropagation();
          scrollEl.scrollTop = Math.max(0, Math.min(maxScroll, scrollTop + event.deltaY));
        };

        const onTouchStart = (event) => {
          if (!event.touches.length) return;
          touchStartY = event.touches[0].clientY;
          touchStartScrollTop = scrollEl.scrollTop;
        };

        const onTouchMove = (event) => {
          if (!event.touches.length) return;
          const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
          if (maxScroll <= 0) return;
          const deltaY = touchStartY - event.touches[0].clientY;
          scrollEl.scrollTop = Math.max(0, Math.min(maxScroll, touchStartScrollTop + deltaY));
          event.preventDefault();
          event.stopPropagation();
        };

        scrollEl.addEventListener('wheel', onWheel, { passive: false });
        scrollEl.addEventListener('touchstart', onTouchStart, { passive: true });
        scrollEl.addEventListener('touchmove', onTouchMove, { passive: false });

        this._modalScrollCleanup = () => {
          scrollEl.removeEventListener('wheel', onWheel);
          scrollEl.removeEventListener('touchstart', onTouchStart);
          scrollEl.removeEventListener('touchmove', onTouchMove);
          this._modalScrollCleanup = null;
        };
      }

      unbindModalScroll() {
        if (this._modalScrollCleanup) this._modalScrollCleanup();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.lockPageScroll();
            super.show(opener);
            this.bindModalScroll();
            this.logScrollDiagnostics('open');
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        productElement.querySelectorAll('[data-sticky-atc]').forEach((el) => el.remove());
        productElement.querySelectorAll('quick-add-modal').forEach((el) => el.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
